/** One identity contract for the till, profiles and future imports. */
export function normaliseIndianMobile(raw: string): string | null {
  if (!/^[+\d\s()-]+$/.test(raw)) return null;
  const compact = raw.replace(/[\s()-]/g, '');
  const national = compact.startsWith('+91')
    ? compact.slice(3)
    : compact.startsWith('0091')
      ? compact.slice(4)
      : compact.length === 12 && compact.startsWith('91')
        ? compact.slice(2)
        : compact.length === 11 && compact.startsWith('0')
          ? compact.slice(1)
          : compact;
  return /^[6-9]\d{9}$/.test(national) ? national : null;
}

export function validCustomerDate(value: string): boolean {
  // A birthday does not require a birth year. Leap-day birthdays remain representable.
  if (/^\d{2}-\d{2}$/.test(value)) {
    const date = new Date(`2000-${value}T00:00:00Z`);
    return Number.isFinite(date.getTime()) && date.toISOString().slice(5, 10) === value;
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return (
    Number.isFinite(date.getTime()) &&
    date.toISOString().slice(0, 10) === value &&
    value >= '1900-01-01' &&
    value <= new Date().toISOString().slice(0, 10)
  );
}

export const CUSTOMER_TEXT_FIELDS = [
  'localName',
  'alternatePhone',
  'email',
  'addressLine1',
  'addressLine2',
  'locality',
  'city',
  'state',
  'stateCode',
  'pincode',
  'countryCode',
  'gstin',
  'businessName',
  'birthday',
  'preferredLanguage',
  'notes',
] as const;
export type CustomerTextField = (typeof CUSTOMER_TEXT_FIELDS)[number];
export type CustomerDetails = Record<CustomerTextField, string | null> & {
  name: string;
  phone: string;
  isB2b: boolean;
  isActive: boolean;
};

export function customerFieldError(field: string, value: string | null): string | null {
  if (value === null || value === '') return null;
  if (field === 'alternatePhone' && !normaliseIndianMobile(value))
    return 'Enter a valid Indian mobile number.';
  if (field === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Enter a valid email address.';
  if (field === 'stateCode' && !/^(0[1-9]|[12]\d|3[0-8]|97|99)$/.test(value))
    return 'Enter a valid state code.';
  if (field === 'pincode' && !/^[1-9]\d{5}$/.test(value)) return 'Enter a six digit pincode.';
  if (field === 'countryCode' && value !== 'IN') return 'Only IN is supported.';
  if (field === 'gstin' && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(value))
    return 'Enter a valid GSTIN.';
  if (field === 'birthday' && !validCustomerDate(value)) return 'Enter a valid past calendar date.';
  if (field === 'preferredLanguage' && !['en', 'ta'].includes(value)) return 'Choose English or Tamil.';
  return null;
}
